import round from 'lodash/round';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

import {
  DAILY,
  ENGINE_HOURS,
  FUEL_USED,
  IDLE_ENGINE_HOURS,
  IDLE_FUEL_USED,
  MONTHLY,
  WEEKLY,
} from '../../Constants/Signal';
import {
  formatUTCForWeeklyUtilizationTile,
  formatUTCFullMonth,
  formatUTCMonthDay,
} from '../../Utils/dateFormatHelpers.util';

const IDLE_ENGINE_HOURS_LABEL = 'Idle Engine Hours';
const IDLE_FUEL_USED_LABEL = 'Idle Fuel Used';
const WORKING_ENGINE_HOURS_LABEL = 'Working Engine Hours';
const WORKING_FUEL_USED_LABEL = 'Working Fuel Used';

export function EngineUtilizationBarChart({
  aggregation,
  data,
  graphIndex,
  graphWidgetIDs,
  renderNextGraph,
  title,
}) {
  const targetData = useMemo(
    () => data?.get?.(aggregation) ?? new Map(),
    [aggregation, data]
  );

  const { categories, series } = useMemo(
    () =>
      Array.from(targetData).reduce(
        (acc, [ts, values]) => {
          const { [ENGINE_HOURS]: total, [IDLE_ENGINE_HOURS]: idle } = values;

          const totalHours = total?.max - total?.min,
            idleHours = idle?.max - idle?.min,
            activeHours = totalHours - idleHours;

          if (Number.isFinite(activeHours)) {
            acc.categories.push(ts);

            acc.series[0].data.push(round(activeHours, 2));
            acc.series[1].data.push(round(idleHours, 2));
          }

          return acc;
        },
        {
          categories: [],
          series: [
            { data: [], name: WORKING_ENGINE_HOURS_LABEL },
            { data: [], name: IDLE_ENGINE_HOURS_LABEL },
          ],
        }
      ),
    [targetData]
  );
  const options = useMemo(
    () => ({
      chart: {
        events: {
          mounted: function () {
            if (graphWidgetIDs.length - 1 <= graphIndex) return;
            renderNextGraph(graphWidgetIDs[graphIndex + 1]);
          },
        },
        fontFamily: '"Roboto", sans-serif',
        id: `${title}-${graphIndex}`,
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: true },
      },
      fill: { opacity: 1 },
      legend: { fontSize: '10px', position: 'bottom' },
      plotOptions: {
        bar: {
          barHeight: '100%',
          dataLabels: {
            total: {
              enabled: true,
              formatter: (value) => round(value, 2),
              style: {
                fontSize: '13px',
                fontWeight: 900,
              },
            },
          },
          horizontal: false,
        },
      },
      theme: { palette: 'palette6' },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        categories,
        labels: {
          formatter: (value) => {
            const date = new Date(value);

            switch (aggregation) {
              case DAILY:
                return formatUTCMonthDay(date);
              case MONTHLY:
                return formatUTCFullMonth(date);
              case WEEKLY:
                return formatUTCForWeeklyUtilizationTile(date);
              default:
                return value;
            }
          },
          rotate: -33,
        },
        type: 'category',
      },
      yaxis: { labels: { formatter: (val) => round(val, 2) } },
    }),
    [
      aggregation,
      categories,
      graphIndex,
      graphWidgetIDs,
      renderNextGraph,
      title,
    ]
  );

  return (
    <Chart
      height="100%"
      options={options}
      series={series}
      type="bar"
      width="100%"
    />
  );
}
EngineUtilizationBarChart.propTypes = {
  aggregation: PropTypes.string,
  data: PropTypes.object,
  graphIndex: PropTypes.number,
  graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
  renderNextGraph: PropTypes.func,
  title: PropTypes.string,
};

export function FuelUtilizationBarChart({
  aggregation,
  data,
  graphIndex,
  graphWidgetIDs,
  renderNextGraph,
  title,
}) {
  const targetData = useMemo(
    () => data?.get?.(aggregation) ?? new Map(),
    [aggregation, data]
  );

  const { categories, series } = useMemo(
    () =>
      Array.from(targetData).reduce(
        (acc, [ts, values]) => {
          const { [FUEL_USED]: total, [IDLE_FUEL_USED]: idle } = values;

          const totalHours = total?.max - total?.min,
            idleHours = idle?.max - idle?.min,
            activeHours = totalHours - idleHours;

          if (Number.isFinite(activeHours)) {
            acc.categories.push(ts);

            acc.series[0].data.push(round(activeHours, 2));
            acc.series[1].data.push(round(idleHours, 2));
          }

          return acc;
        },
        {
          categories: [],
          series: [
            { data: [], name: WORKING_FUEL_USED_LABEL },
            { data: [], name: IDLE_FUEL_USED_LABEL },
          ],
        }
      ),
    [targetData]
  );
  const options = useMemo(
    () => ({
      chart: {
        events: {
          mounted: function () {
            if (graphWidgetIDs.length - 1 <= graphIndex) return;
            renderNextGraph(graphWidgetIDs[graphIndex + 1]);
          },
        },
        fontFamily: '"Roboto", sans-serif',
        id: `${title}-${graphIndex}`,
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: true },
      },
      fill: { opacity: 1 },
      legend: { fontSize: '10px', position: 'bottom' },
      plotOptions: {
        bar: {
          barHeight: '100%',
          dataLabels: {
            total: {
              enabled: true,
              formatter: (value) => round(value, 2),
              style: {
                fontSize: '13px',
                fontWeight: 900,
              },
            },
          },
          horizontal: false,
        },
      },
      theme: { palette: 'palette5' },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        categories,
        labels: {
          formatter: (value) => {
            const date = new Date(value);

            switch (aggregation) {
              case DAILY:
                return formatUTCMonthDay(date);
              case MONTHLY:
                return formatUTCFullMonth(date);
              case WEEKLY:
                return formatUTCForWeeklyUtilizationTile(date);
              default:
                return value;
            }
          },
          rotate: -25,
        },
        type: 'category',
      },
      yaxis: { labels: { formatter: (val) => round(val, 2) } },
    }),
    [
      aggregation,
      categories,
      graphIndex,
      graphWidgetIDs,
      renderNextGraph,
      title,
    ]
  );

  return (
    <Chart
      height="100%"
      options={options}
      series={series}
      type="bar"
      width="100%"
    />
  );
}
FuelUtilizationBarChart.propTypes = {
  aggregation: PropTypes.string,
  data: PropTypes.object,
  graphIndex: PropTypes.number,
  graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
  renderNextGraph: PropTypes.func,
  title: PropTypes.string,
};
